/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import convert, { unit, measure } from '@g17eco/convert-units';
import { TableColumnType, UniversalTrackerPlain, UtrValueType } from '../types/universalTracker';
import { UnitConfig } from '@g17eco/types/survey';

const converter = convert(0);
type UtrUnitInfo = Pick<UniversalTrackerPlain, 'unit' | 'unitType' | 'numberScale' | 'valueType'>;

export const UnitTypes = {
  currency: 'currency',
  numberScale: 'numberScale',
};

export type AllowedUnit = keyof Omit<UnitConfig, 'numberScale' | 'currency'>;

export const whitelist: { [k in AllowedUnit]: string[] | undefined } = {
  area: ['m2', 'ha', 'km2', 'ft2', 'mi2'],
  length: ['mm', 'cm', 'm', 'km', 'in', 'yd', 'ft', 'mi'],
  mass: ['kg', 'mt', 't', 'kt', 'lb'],
  volume: ['ml', 'l', 'kl', 'Ml', 'm3', 'Mcm', 'km3', 'gal', 'bbl', 'mmscf'],
  time: ['h', 'd', 'week', 'month', 'year'],
  energy: ['kWh', 'MWh', 'J', 'kJ', 'mJ', 'GJ', 'mmbtu', 'thm'],
  co2Emissions: ['tons/CO2e', 'kg/CO2e'],
  partsPer: ['ppm','ppb','ppt','ppq'],
};

export interface UnitOption {
  abbr: string;
  measure?: string;
  plural?: string;
  singular: string;
  system?: string;
}

export const getUnitsForType = (type: string | undefined) => {
  const unitType = type === UnitTypes.currency ? UnitTypes.numberScale : type;
  const options = unitType ? converter.list(unitType as measure) : []

  // Only whitelisted options
  const whitelistElement = type ? whitelist[type as AllowedUnit] : undefined;
  if (whitelistElement) {
    return options.filter(({ abbr }: { abbr: string }) => whitelistElement.includes(abbr));
  }

  // Everything
  return options;
};

/** @throws Error */
export const getUnitDescription = (code: string | undefined) => converter.describe(code as unit);

export const convertValue = <T>(value: T, from?: string, to?: string): T => {
  if (!from || !to) {
    return value;
  }

  try {
    return convert(value as number).from(from as unit).to(to  as unit) as T;
  } catch {
    return value;
  }
};


export const getUnitText = ({ valueType, unit, unitType }: UtrUnitInfo, fallback = '-'): string => {
  if (valueType === UtrValueType.Percentage) {
    return '%';
  }

  if (!unitType) {
    return fallback;
  }
  if (unitType === UnitTypes.currency) {
    return unit ?? fallback;
  }

  if (!unit) {
    return fallback;
  }

  try {
    const descr = getUnitDescription(unit);
    return descr.plural;
  } catch {
    return fallback;
  }
};

export const getUnitDesc = ({
  unit,
  unitType,
  unitConfig,
  fallback = '',
}: Pick<UtrUnitInfo, 'unit' | 'unitType'> & { unitConfig?: UnitConfig; fallback?: string }): string => {
  if (!unitType || !unit) {
    return fallback;
  }
  if (unitType === UnitTypes.currency) {
    return unitConfig?.currency ?? unit ?? fallback;
  }

  if (!unit) {
    return fallback;
  }

  try {
    return getUnitDescription(unit).plural;
  } catch {
    return fallback;
  }
};

export const getNumberScaleText = (numberScale: string | undefined, fallback = '-'): string => {
  if (!numberScale) {
    return fallback;
  }
  try {
    const descr = getUnitDescription(numberScale);
    return descr.plural;
  } catch {
    return fallback;
  }
}

interface UnitFromTypeLookup extends  Pick<UniversalTrackerPlain, 'unitType' | 'unit'> {
  unitConfig?: UnitConfig
}
const getUnitFromUnitType = ({ unit, unitType, unitConfig }: UnitFromTypeLookup) => {
  if (!unitConfig || unitType !== UnitTypes.currency) {
    return unit;
  }
  return unitConfig.currency;
}

export const getUtrUnitInfo = (utr: UniversalTrackerPlain, tableColumn?: string, unitConfig?: UnitConfig): UtrUnitInfo | undefined => {
  if (!tableColumn) {
    return {
      valueType: utr.valueType,
      unit: getUnitFromUnitType({ unit: utr.unit, unitType: utr.unitType, unitConfig }),
      unitType: utr.unitType,
      numberScale: utr.numberScale,
    }
  }

  const data = utr.valueValidation?.table?.columns.find(col => col.code === tableColumn);
  if (!data) {
    return undefined;
  }

  // Override to percentage
  const isPercentage = data.type === TableColumnType.Number && data.validation?.min === 0 && data.validation?.max === 100;
  return {
    valueType: isPercentage ? UtrValueType.Percentage : data.type,
    unit: getUnitFromUnitType({ unit: data.unit, unitType: data.unitType, unitConfig }),
    unitType: data.unitType,
    numberScale: data.numberScale,
  }
};

export const getCurrencyList = () => {
  return [
    {
      singular: 'Euros',
      abbr: 'EUR'
    },
    {
      singular: 'British Pounds',
      abbr: 'GBP'
    },
    {
      singular: 'US Dollars',
      abbr: 'USD'
    },
    {
      singular: 'Australian Dollar',
      abbr: 'AUD'
    },
    {
      singular: 'Japanese Yen',
      abbr: 'JPY'
    },
    {
      singular: 'Yuan Renminbi',
      abbr: 'CNY'
    },
    {
      singular: 'Hong Kong Dollar',
      abbr: 'HKD'
    },
    {
      singular: 'Canadian Dollar',
      abbr: 'CAD'
    },
    {
      singular: 'Indonesian Rupiah',
      abbr: 'IDR'
    },
    {
      singular: 'Kenyan Shilling',
      abbr: 'KES'
    },
    {
      singular: 'Singapore Dollar',
      abbr: 'SGD'
    },
    {
      singular: 'Malaysian Ringgit',
      abbr: 'MYR'
    },
    {
      singular: 'Swiss Franc',
      abbr: 'CHF'
    },
    {
      singular: 'United Arab Emirates Dirham',
      abbr: 'AED',
    },
    {
      singular: 'Saudi Riyal',
      abbr: 'SAR',
    },
    {
      singular: 'Egyptian Pound',
      abbr: 'EGP',
    },
  ]
}

export const getCurrencyOptions = () => getCurrencyList().map(item => ({ value: item.abbr, label: item.singular }));

export const convertUnitNumberScale = (params: {
  value: number;
  from: { unit: string | undefined; numberScale: string | undefined };
  to: { unit: string | undefined; numberScale: string | undefined };
}) => {
  const { value, from, to } = params;

  const convertedNumberScaleValue = convertValue(value, from.numberScale, to.numberScale);
  const convertedUnitValue = convertValue(convertedNumberScaleValue, from.unit, to.unit);
  return convertedUnitValue;
};

export const getAvailableUnits = () => converter.getAvailableUnits();

export const getUnitCodeByName = ({
  name,
  unitType,
  fallback = '',
}: {
  name?: string;
  unitType?: string;
  fallback?: string;
}) => {
  if (!name || !unitType) {
    return fallback;
  }

  const units = getUnitsForType(unitType);
  const unit = units.find(({ plural, singular }: { plural: string; singular: string }) =>
    [plural, singular].includes(name),
  );

  const returnValue = unit?.abbr ?? fallback;

  return returnValue;
};
